import { Card, Skeleton, Stack, Typography } from "@mui/material";
import { memo, useEffect, useState } from "react";
import FilterDonut from "./FilterDonut";
import ReloadButton from "../../../common/reload-button/ReloadButton";
import DonutChart, { IPieData } from "../../../common/PieChart";
import { useNotificationContext } from "../../../../context/notificationContext";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import errorResolver from "../../../../utils/error-resolver";
import { format } from "date-fns";
import { filterOptions } from "./filterOptions";
import MuiLinearProgress from "../../common/MuiLinearProgress";
import {
  BroadcastingDetailsRequest,
  BroadcastingReportListColumnFilterable,
  VoiceBroadCastReportSchema,
} from "../../../../services/Endpoints/broadcast";
import { FilterOperatorEnum } from "../../../../services/Schemas/ListQueryString";
import { useWorkspaceContext } from "../../../../context/workspaceContext";
import { formatCurrency } from "../../../../utils/currency-formater";

interface IBroadCastingMedSecProps {
  type?: string;
  id?: number | string;
  loading?: boolean;
  initialReports?: VoiceBroadCastReportSchema[] | null;
}

const handleStatus = (reports: VoiceBroadCastReportSchema[]) => {
  let successTotal = 0;
  let failedTotal = 0;
  let pendingTotal = 0;

  const failedStatuses = [
    "failed",
    "blocked",
    "no_answer",
    "busy",
    "rejected",
    "unreachable",
    "device_off",
    "invalid_number",
    "canceled",
    "retry",
  ];

  const pendingStatuses = ["created", "waiting_for_send", "pending", "sending" , "queued"];

  reports.forEach((report) => {
    if (!report.status || report.total <= 0) return;

    if (report.status === "answered") {
      successTotal += report.total;
    } else if (failedStatuses.includes(report.status)) {
      failedTotal += report.total;
    } else if (!failedStatuses.includes(report.status)) {
      pendingTotal += report.total;
    } else {
      pendingTotal += report.total;
      console.log("pendingTotal else",pendingTotal , report);
    }
    
  });

  const result: IPieData[] = [];

  if (successTotal > 0) {
    result.push({
      name: "تماس‌های موفق",
      value: successTotal,
      color: "#27AE60", // سبز
      valueDisplay: " تماس",
    });
  }

  if (failedTotal > 0) {
    result.push({
      name: "تماس‌های ناموفق",
      value: failedTotal,
      color: "#E74C3C", // قرمز
      valueDisplay: " تماس",
    });
  }

  if (pendingTotal > 0) {
    result.push({
      name: "در صف ارسال",
      value: pendingTotal,
      color: "#7F8C8D", // خاکستری
      valueDisplay: " تماس",
    });
  }

  return result;
};

const BroadCastStatusReport = ({
  id,
  type,
  loading,
  initialReports = null,
}: Readonly<IBroadCastingMedSecProps>) => {
  const [filter1, setFilter1] = useState(filterOptions[0].value.start);
  const [reports, setReports] = useState<VoiceBroadCastReportSchema[]>([]);
  const [timeFilter, setTimeFilter] = useState<string[] | null>(null);
  const [waiting, setWaiting] = useState(false);
  const [reload, setReload] = useState(false);

  const { setNotification } = useNotificationContext();
  const protectedInstance = useProtectedAxiosInstance();

  const handleReload = () => setReload(!reload);

  const getData = async () => {
    if (!id) return;
    try {
      setWaiting(true);
      const broadcastingDetailsRequest = new BroadcastingDetailsRequest(
        protectedInstance
      );

      const { data: _data } = await broadcastingDetailsRequest.report(
        {
          broadcastingNumber: id.toString(),
        },
        {
          filters: timeFilter
            ? [
                {
                  column: BroadcastingReportListColumnFilterable.UPDATED_AT,
                  operator: FilterOperatorEnum.BETWEEN,
                  value: timeFilter,
                },
              ]
            : [],
        }
      );

      setReports(_data.number_reports);
    } catch (error: any) {
      errorResolver({ error, setNotification });
      console.error(error.message);
    } finally {
      setWaiting(false);
    }
  };

  useEffect(() => {
    initialReports && getData();
  }, [timeFilter, reload]);

  useEffect(() => {
    initialReports && setReports(initialReports);
  }, [initialReports]);

  const handleTimeFilter = (filter1: { start: string; end: string }) => {
    if (waiting) return;
    setFilter1(filter1.start);
    setTimeFilter([
      format(filter1.start, "yyyy-MM-dd HH:mm:ss"),
      format(filter1.end, "yyyy-MM-dd HH:mm:ss"),
    ]);
  };

  return (
    <Stack position="relative">
      {waiting && <MuiLinearProgress />}
      <Card
        height="100%"
        bgcolor="#fff"
        component={Stack}
        gap="10px"
        sx={{
          borderRadius: "8px",
          py: "14px",
          px: "16px",
          borderColor: (theme) => theme.palette.others.borderColor,
          borderWidth: "1px",
          borderStyle: "solid",
          boxShadow: "none",
          flex: "1",
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" gap="10px" justifyContent="start">
            {!loading ? (
              <FilterDonut
                setOption={handleTimeFilter}
                optionsList={filterOptions}
                active={
                  filterOptions.filter((itm) => itm.value.start == filter1)[0]
                    ?.key
                }
              />
            ) : (
              Array(5)
                .fill("")
                .map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    width="80px"
                    height="20px"
                  />
                ))
            )}
          </Stack>
          {!loading ? (
            <ReloadButton reloadAction={handleReload} />
          ) : (
            <Skeleton variant="rectangular" width="40px" height="40px" />
          )}
        </Stack>

        {!loading ? (
          <Typography
            variant="h6MediumBold"
            color={(thm) => thm.palette.accent.main}
          >
            گزارش تماس‌ها (
            {formatCurrency(
              reports.reduce((prev, cur) => prev + cur.total, 0).toString()
            )}
            )
          </Typography>
        ) : (
          <>
            <Skeleton variant="rectangular" width="200px" height="20px" />
            <Skeleton variant="rectangular" width="500px" height="20px" />
          </>
        )}

        <Stack justifyContent="start" alignItems="center" width="100%">
          {reports ? (
            <DonutChart
              data={handleStatus(reports)}
              pieWrapSx={{
                flexDirection: "row-reverse",
                alignItems: "center",
                width: "100%",
                justifyContent: { xs: "start", md: "space-between" },
              }}
              legendSx={{
                flexDirection: "column",
              }}
              loading={(waiting && reports.length === 0) || loading}
            />
          ) : (
            <Typography
              sx={{
                textAlign: "center",
                width: "100%",
                py: "30px",
              }}
            >
              {!waiting && "دیتایی جهت نمایش وجود ندارد."}
            </Typography>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};

export default memo(BroadCastStatusReport);
